'use client';

import { useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import type { BoardEntry, StoredProblem } from '@/components/whiteboard-v2/types';
import type { DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';

const LiveWhiteboard = dynamic(
  () => import('@/components/whiteboard-v2/LiveWhiteboard'),
  { ssr: false, loading: () => <div className="flex h-[400px] items-center justify-center rounded-xl bg-[#F8FAFC] text-sm text-[#8A96A8]">Loading AI Tutor...</div> },
);

const DEMO_PROBLEM: StoredProblem = {
  id: 'demo-fraction',
  question: 'Simplify the fraction 18/24 to its lowest terms.',
  answer: '3/4',
  options: [],
  type: 'blank_filling',
  subject: 'math',
};

const PREVIEW_ENTRIES: BoardEntry[] = [
  { id: 'p1', content: 'Find the GCF', renderedContent: 'Find the Greatest Common Factor (GCF)', type: 'subheading', timestamp: 0 },
  { id: 'p2', content: 'Factors of 18: 1, 2, 3, 6, 9, 18', renderedContent: '$\\text{Factors of 18: } 1, 2, 3, 6, 9, 18$', type: 'step', timestamp: 0 },
  { id: 'p3', content: 'Factors of 24: 1, 2, 3, 4, 6, 8, 12, 24', renderedContent: '$\\text{Factors of 24: } 1, 2, 3, 4, 6, 8, 12, 24$', type: 'step', timestamp: 0 },
  { id: 'p4', content: 'GCF = 6', renderedContent: '$\\text{GCF} = 6$', type: 'step', timestamp: 0 },
  { id: 'p5', content: 'Divide both by GCF', renderedContent: 'Divide both numerator and denominator by GCF', type: 'subheading', timestamp: 0 },
  { id: 'p6', content: '18/24 = (18÷6)/(24÷6) = 3/4', renderedContent: '$\\dfrac{18}{24} = \\dfrac{18 \\div 6}{24 \\div 6} = \\boxed{\\dfrac{3}{4}}$', type: 'step', timestamp: 0 },
];

export default function LiveTutorDemo() {
  const [started, setStarted] = useState(false);
  const [startSignal, setStartSignal] = useState(0);

  const selection: DemoSelection = { kind: 'problem', problem: DEMO_PROBLEM };

  const handleStart = useCallback(() => {
    setStarted(true);
    setStartSignal((s) => s + 1);
  }, []);

  return (
    <div className="mx-auto max-w-[780px]">
      <LiveWhiteboard
        selection={started ? selection : null}
        previewEntries={PREVIEW_ENTRIES}
        startSessionSignal={startSignal}
        desktopWidth={700}
        desktopHeight={450}
        onSessionEnd={() => setStarted(false)}
      />
      {!started && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleStart}
            className="inline-flex items-center gap-2 rounded-lg bg-[#42C52D] px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#38a827]"
          >
            Try AI Tutor Now
            <span aria-hidden="true">&rarr;</span>
          </button>
          <p className="mt-2 text-xs text-[#8A96A8]">No account needed. Talk to your AI tutor with voice.</p>
        </div>
      )}
    </div>
  );
}
